import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, finalize, Observable, of, shareReplay, tap, throwError } from 'rxjs';

const VAULT_AUTH_ME_CIRCUIT_KEY = 'vault_auth_me_circuit_open';
const CACHE_TTL_MS = 30_000;

export function isVaultAuthMeCircuitOpen(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(VAULT_AUTH_ME_CIRCUIT_KEY) === '1';
}

export function openVaultAuthMeCircuit(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(VAULT_AUTH_ME_CIRCUIT_KEY, '1');
  }
}

export function resetVaultAuthMeCircuit(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(VAULT_AUTH_ME_CIRCUIT_KEY);
  }
}

/**
 * Cliente único para `GET /api/auth/me`: deduplica peticiones paralelas y, tras un 5xx,
 * deja de reintentar en la sesión (my-tenants + dashboard/stats cubren el fallback).
 */
@Injectable({ providedIn: 'root' })
export class VaultAuthMeService {
  private readonly http = inject(HttpClient);
  private inFlight: Observable<unknown | null> | null = null;
  private cached: unknown | null = null;
  private cachedAt = 0;

  fetchRaw$(): Observable<unknown | null> {
    if (typeof window === 'undefined') return of(null);
    if (isVaultAuthMeCircuitOpen()) {
      return of(this.cached);
    }

    const now = Date.now();
    if (this.cached !== null && now - this.cachedAt < CACHE_TTL_MS) {
      return of(this.cached);
    }
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.http.get<unknown>('/api/auth/me').pipe(
      tap((raw) => {
        this.cached = raw;
        this.cachedAt = Date.now();
        resetVaultAuthMeCircuit();
      }),
      catchError((err: { status?: number; error?: unknown }) => {
        if (err?.status === 403) {
          return throwError(() => err);
        }
        if ((err?.status ?? 0) >= 500) {
          openVaultAuthMeCircuit();
        }
        return of(null);
      }),
      finalize(() => {
        this.inFlight = null;
      }),
      shareReplay(1),
    );

    return this.inFlight;
  }

  clearCache(): void {
    this.inFlight = null;
    this.cached = null;
    this.cachedAt = 0;
  }
}
